import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, MapPin } from 'lucide-react';
import { API_BASE_URL } from '../../../api';

const DEBOUNCE_MS = 350;

/**
 * Landmark/place search for the delivery map — type "Kikuyu Stage" and get
 * matching places back (via /api/geocode/search, MapTiler geocoding),
 * complementary to the existing click-the-map / type-the-address modes, not
 * a replacement for either. Selecting a result hands {lat, lng,
 * radiusMeters, isWideArea, placeName} up to the caller, which moves the
 * map's pin and draws the uncertainty radius (see applySearchResultRef in
 * EcommerceWebsite.jsx).
 */
const LocationSearchBox = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/geocode/search?q=${encodeURIComponent(trimmed)}`);
        const data = await res.json();
        setResults(Array.isArray(data.results) ? data.results : []);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close the dropdown on an outside click, same pattern as any other
  // lightweight combobox — no portal needed since this never needs to
  // escape the checkout modal's own stacking context.
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleSelect = useCallback((result) => {
    setQuery(result.placeName);
    setIsOpen(false);
    setResults([]);
    onSelect(result);
  }, [onSelect]);

  return (
    <div ref={containerRef} className="relative mb-3">
      <div className="relative">
        <Search className="w-4 h-4 text-ink-faint absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search for a landmark, estate, or street…"
          className="w-full border border-ink-faint rounded-sm pl-9 pr-3.5 py-2.5 text-sm bg-surface focus:ring-1 focus:ring-accent focus:border-accent"
        />
      </div>

      {/* z-[1000]: Leaflet sets its own internal panes to z-index ~200-700
          (tilePane, markerPane, popupPane, etc.) — a plain z-20 here sat
          underneath all of them, so the dropdown rendered but was
          completely hidden behind the map below it. Verified by
          reproducing the exact stacking locally before raising this. */}
      {isOpen && (query.trim().length >= 2) && (
        <div className="absolute z-[1000] top-full left-0 right-0 mt-1 bg-surface border border-line rounded-sm shadow-lg max-h-64 overflow-y-auto">
          {isLoading && (
            <p className="px-3.5 py-2.5 text-xs text-ink-faint">Searching…</p>
          )}
          {!isLoading && results.length === 0 && (
            <p className="px-3.5 py-2.5 text-xs text-ink-faint">No matches — try a nearby town or landmark name.</p>
          )}
          {!isLoading && results.map((r, idx) => (
            <button
              key={`${r.placeName}-${idx}`}
              type="button"
              onClick={() => handleSelect(r)}
              className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-paper transition-colors border-b border-line last:border-b-0"
            >
              <span className="flex items-center gap-2 text-ink min-w-0">
                <MapPin className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />
                <span className="truncate">{r.placeName}</span>
              </span>
              <span className={`dpc-label text-[9px] flex-shrink-0 ${r.isWideArea ? 'text-ink-faint' : 'text-accent'}`}>
                {r.isWideArea ? 'Area' : 'Exact'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationSearchBox;
